import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Heuristica: el error viene de una interrupcion temporal de red/servidor
 * (ej. un 504 del auth de Supabase cuando GoTrue no alcanza su Postgres).
 * En ese caso el mensaje es de "reintenta en un momento", no de "algo se rompio".
 */
const TRANSIENT_RE =
  /\b504\b|timeout|timed out|network|failed to fetch|failed to connect|deadline|request_timeout/i;

/**
 * Error boundary global de rutas — se registra como `defaultErrorComponent`
 * del router (ver `src/app/router.tsx`).
 *
 * Sin esto, cualquier error lanzado desde un `beforeLoad`/`loader` (el caso
 * real: un 504 transitorio de Supabase Auth al refrescar la sesion en el guard
 * de `_app`) dejaba la pantalla EN BLANCO, sin forma de recuperarse mas alla de
 * un hard refresh manual. Ver issue #182.
 */
export function RouteErrorBoundary({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const [retrying, setRetrying] = useState(false);

  const message = error instanceof Error ? error.message : String(error ?? '');
  const isTransient = TRANSIENT_RE.test(message);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      // Limpia el estado de error de la ruta y vuelve a correr sus loaders.
      reset();
      await router.invalidate();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-surface-canvas p-6">
      <div className="w-full max-w-md text-center">
        <h1 className="text-lg font-semibold text-ink">
          {isTransient ? 'Problema de conexión temporal' : 'Algo salió mal'}
        </h1>
        <p className="mt-2 text-sm text-body">
          {isTransient
            ? 'No pudimos cargar la página por una interrupción temporal del servidor. Suele resolverse en unos segundos.'
            : 'Ocurrió un error inesperado al cargar la página. Puedes reintentar o recargar.'}
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button type="button" onClick={handleRetry} disabled={retrying}>
            {retrying ? 'Reintentando…' : 'Reintentar'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => window.location.reload()}>
            Recargar página
          </Button>
        </div>
      </div>
    </div>
  );
}
